// Central content source for the Amden Growth page.
// Structured so a future website admin area can manage every field
// without touching page components.

export const GROWTH_SEO = {
  title: 'Marketing Strategy, Social Media & Website Support | Amden Group',
  description:
    'Build your brand and generate better opportunities with marketing strategy, social media support, website services and practical training from Amden Group.',
};

export const GROWTH_HERO = {
  eyebrow: 'Amden Growth',
  headingLines: ['Build your brand.', 'Grow your audience.', 'Win better business.'],
  accentLineIndex: 2,
  body: 'Amden helps businesses sharpen their message, strengthen their digital presence and build practical marketing strategies that support measurable commercial growth.',
  primaryCta: {
    label: 'Discuss Your Growth',
    href: '/?service=growth#solution-finder',
  },
  secondaryCta: {
    label: 'Explore Growth Services',
    target: '#growth-services',
  },
  image: {
    src: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=1200&q=80',
    alt: 'Business owner and adviser reviewing a practical marketing plan together',
    width: 1200,
    height: 900,
  },
  floatingCard: {
    icon: 'users',
    label: 'Commercially focused',
    text: 'Marketing activity connected directly to enquiries, sales and growth.',
  },
};

export const GROWTH_WHAT_WHO = {
  whatHeading: 'What We Do',
  whatCopy:
    'From strategic advice to hands-on execution, Amden helps businesses create a clearer market position, improve their digital presence and build consistent marketing activity that supports sales and long-term growth.',
  whatAreas: [
    'Marketing strategy development',
    'Brand positioning and messaging',
    'Social media planning and support',
    'Website strategy, design and improvement',
    'Content planning and campaign support',
    'Lead-generation planning',
    'Sales and marketing alignment',
    'Marketing performance and reporting',
    'Internal marketing training',
    'Ongoing advisory and implementation support',
  ],
  whoHeading: 'Who It’s For',
  whoCopy:
    'Our Growth services support businesses that want greater visibility, more consistent marketing and a clearer route from activity to commercial results.',
  whoAreas: [
    'Businesses with inconsistent marketing activity',
    'Companies lacking a clear marketing strategy',
    'Organisations with weak or outdated websites',
    'Teams needing practical social media support',
    'Businesses struggling to explain their value',
    'Companies seeking stronger lead generation',
    'Internal teams requiring marketing training',
    'Growing businesses without senior marketing expertise',
  ],
};

export const GROWTH_PROBLEMS = {
  eyebrow: 'Problems We Solve',
  heading: 'Problems We Solve',
  intro:
    'Marketing problems are often caused by unclear positioning, inconsistent execution or activity that is disconnected from commercial goals. We help businesses establish the right strategy and turn it into practical action.',
  items: [
    {
      id: 'low-visibility',
      icon: 'eye-off',
      title: 'Low Visibility',
      description: 'The right customers do not see or recognise the business often enough.',
      howWeHelp:
        'We sharpen your positioning and build a consistent, prioritised presence across the channels where your customers actually are.',
    },
    {
      id: 'weak-digital-presence',
      icon: 'globe',
      title: 'Weak Digital Presence',
      description: 'The website and online channels do not reflect the quality or capability of the business.',
      howWeHelp:
        'We bring your website and online channels up to the standard of the business itself — credible, current and built to convert.',
    },
    {
      id: 'brand-confusion',
      icon: 'palette',
      title: 'Brand Confusion',
      description: 'The business lacks a clear message, position or consistent visual identity.',
      howWeHelp:
        'We define a clear position and message, then apply it consistently so customers understand exactly why they should choose you.',
    },
    {
      id: 'inconsistent-social',
      icon: 'calendar-x',
      title: 'Inconsistent Social Media',
      description: 'Content is irregular, reactive or disconnected from wider business goals.',
      howWeHelp:
        'We put structure around social media — planned content tied to business goals, delivered consistently rather than reactively.',
    },
    {
      id: 'poor-website-performance',
      icon: 'monitor-x',
      title: 'Poor Website Performance',
      description: 'The website looks dated, is difficult to update or fails to generate meaningful enquiries.',
      howWeHelp:
        'We improve strategy, content and usability so the website earns trust and generates genuine enquiries, not just visits.',
    },
    {
      id: 'no-marketing-strategy',
      icon: 'signpost',
      title: 'No Clear Marketing Strategy',
      description: 'Marketing activity takes place without defined audiences, priorities, measures or outcomes.',
      howWeHelp:
        'We build a focused strategy — defined audiences, clear priorities and measures — so every activity has a purpose.',
    },
    {
      id: 'weak-lead-generation',
      icon: 'magnet',
      title: 'Weak Lead Generation',
      description: 'Marketing creates attention but does not consistently support enquiries or sales opportunities.',
      howWeHelp:
        'We connect marketing activity to a practical lead-generation plan, so attention turns into enquiries and opportunities.',
    },
    {
      id: 'sales-marketing-misalignment',
      icon: 'arrow-left-right',
      title: 'Sales and Marketing Misalignment',
      description: 'Marketing activity and sales priorities are not working towards the same commercial goals.',
      howWeHelp:
        'We align marketing priorities with sales goals, so both sides of the business pull in the same commercial direction.',
    },
    {
      id: 'limited-internal-capability',
      icon: 'book-open',
      title: 'Limited Internal Capability',
      description: 'The team lacks the skills, structure or confidence to manage marketing effectively.',
      howWeHelp:
        'We train owners, managers and internal teams to plan, deliver and measure marketing with confidence.',
    },
    {
      id: 'stagnant-growth',
      icon: 'trending-down',
      title: 'Stagnant Growth',
      description: 'The business is active but not achieving sufficient reach, enquiries or commercial momentum.',
      howWeHelp:
        'We identify where activity is falling short and rebuild the approach around measurable commercial outcomes.',
    },
  ],
};

/**
 * Growth services & packages — data-driven and future-purchase-ready.
 *
 * DEVELOPER NOTE (future e-commerce):
 * - `slug` + `href` are the stable identifiers for future service-detail pages.
 * - `purchaseMode` supports: direct_purchase | request_quote | book_consultation | contact_us
 * - `purchasable` stays false and `price` / `vat` stay null until pricing,
 *   terms and Stripe products are approved by Amden Group.
 * - `stripeProductId` / `stripePriceId` are reserved for the approved Stripe catalogue.
 * - `fullDescription`, `format`, `duration`, `included` and `audience` are
 *   intentionally empty until approved content is supplied — do not invent them.
 * - Ongoing management, new website development and tailored consultancy
 *   default to enquiry/consultation unless later configured otherwise.
 */
export const GROWTH_SERVICES = [
  {
    slug: 'social-media-support',
    name: 'Social Media Support',
    category: 'Social Media',
    shortDescription:
      'Planning, content development and structured support to help your business maintain a professional and consistent social media presence.',
    fullDescription: null,
    icon: 'share-2',
    featuredImage: null,
    format: null,
    duration: null,
    included: [],
    audience: null,
    price: null,
    vat: null,
    purchaseMode: 'request_quote',
    purchasable: false,
    stripeProductId: null,
    stripePriceId: null,
    href: '/growth/social-media-support',
    cta: 'details',
    availability: 'available',
    displayOrder: 1,
  },
  {
    slug: 'marketing-advice',
    name: 'Marketing Advice',
    category: 'Advisory',
    shortDescription:
      'Practical senior-level marketing guidance to help you prioritise activity, make better decisions and connect marketing with commercial objectives.',
    fullDescription: null,
    icon: 'lightbulb',
    featuredImage: null,
    format: null,
    duration: null,
    included: [],
    audience: null,
    price: null,
    vat: null,
    purchaseMode: 'book_consultation',
    purchasable: false,
    stripeProductId: null,
    stripePriceId: null,
    href: '/growth/marketing-advice',
    cta: 'discuss',
    availability: 'available',
    displayOrder: 2,
  },
  {
    slug: 'website-support',
    name: 'Website Support',
    category: 'Websites',
    shortDescription:
      'Website strategy, design, content and ongoing improvement focused on credibility, usability, visibility and lead generation.',
    fullDescription: null,
    icon: 'monitor',
    featuredImage: null,
    format: null,
    duration: null,
    included: [],
    audience: null,
    price: null,
    vat: null,
    purchaseMode: 'request_quote',
    purchasable: false,
    stripeProductId: null,
    stripePriceId: null,
    href: '/growth/website-support',
    cta: 'details',
    availability: 'available',
    displayOrder: 3,
  },
  {
    slug: 'strategy-building',
    name: 'Strategy Building',
    category: 'Strategy',
    shortDescription:
      'A structured marketing strategy covering audiences, positioning, messaging, channels, priorities, actions and measures.',
    fullDescription: null,
    icon: 'crosshair',
    featuredImage: null,
    format: null,
    duration: null,
    included: [],
    audience: null,
    price: null,
    vat: null,
    purchaseMode: 'request_quote',
    purchasable: false,
    stripeProductId: null,
    stripePriceId: null,
    href: '/growth/strategy-building',
    cta: 'details',
    availability: 'available',
    displayOrder: 4,
  },
  {
    slug: 'marketing-training',
    name: 'Marketing Training',
    category: 'Training',
    shortDescription:
      'Practical training that helps owners, managers and internal teams plan content, use marketing channels effectively and measure what is working.',
    fullDescription: null,
    icon: 'presentation',
    featuredImage: null,
    format: null,
    duration: null,
    included: [],
    audience: null,
    price: null,
    vat: null,
    purchaseMode: 'request_quote',
    purchasable: false,
    stripeProductId: null,
    stripePriceId: null,
    href: '/growth/marketing-training',
    cta: 'details',
    availability: 'available',
    displayOrder: 5,
  },
];

export const GROWTH_SERVICES_SECTION = {
  eyebrow: 'Growth Services',
  heading: 'Practical marketing support built around your business',
  intro:
    'Choose a defined service or package, or speak with Amden about tailored and ongoing growth support.',
};

export const GROWTH_DELIVERY = {
  eyebrow: 'How We Deliver',
  heading: 'A structured route from activity to growth',
  stages: [
    {
      number: '01',
      title: 'Discovery',
      text: 'We understand your business, market, audience, current activity and commercial objectives.',
    },
    {
      number: '02',
      title: 'Strategy',
      text: 'We create a focused plan covering priorities, messages, channels, responsibilities and measurable goals.',
    },
    {
      number: '03',
      title: 'Execution',
      text: 'We work alongside your team to implement campaigns, content, websites and agreed marketing activity.',
    },
    {
      number: '04',
      title: 'Optimisation',
      text: 'We review performance, learn from results and continuously improve the approach.',
    },
  ],
  diagramLabel:
    'Continuous growth cycle: discovery, strategy, execution, then optimisation.',
};

export const GROWTH_CASE_STUDY = {
  eyebrow: 'Case Study',
  heading: 'Greenville IDC Multi-Region Growth Strategy',
  intro:
    'Strategy, positioning and commercial alignment across four regions — structured growth, not just content creation.',
  challenge: {
    title: 'Challenge',
    text: 'Greenville IDC required clearer strategic direction, stronger marketing consistency, and improved operational alignment across Ireland, Northern Ireland, Scotland, and England as the business continued to grow across multiple regions.',
  },
  solution: {
    title: 'Solution',
    text: 'Amden Growth provided strategic marketing support, regional alignment, and ongoing consultancy designed to improve visibility, strengthen brand consistency, and create a more structured approach to growth across all four regions.',
  },
  results: {
    title: 'Results',
    points: [
      'Improved clarity and strategic direction',
      'Stronger branding and regional consistency',
      'Better alignment across multiple regions',
    ],
  },
  cta: { label: 'Read Full Case Study', href: '/case-studies/greenville-idc' },
};

export const GROWTH_CTA = {
  index: '06',
  eyebrow: 'Next step',
  heading: 'Ready to build a clearer path to growth?',
  copy: 'Let’s identify where your marketing can work harder and create a practical strategy for stronger visibility, better enquiries and commercial growth.',
  primaryLabel: 'Discuss Your Growth',
  primaryHref: '/?service=growth#solution-finder',
  secondaryLabel: 'Explore Growth Services',
  secondaryHref: '#growth-services',
};
